import { result, sha256 } from "./core.js";
import type { VerificationCheck, VerificationResult } from "./core.js";

const MAX_MULTIPLIER = 1000;
const HOUSE_RETURN = 0.99;

export async function calculateCrashMultiplier(serverSeed: string, roundId: string | number): Promise<string> {
  const digest = await sha256(`${serverSeed}:crash926:${roundId}`);
  const random = Number.parseInt(digest.slice(0, 13), 16) / 2 ** 52;
  const multiplier = Math.min(MAX_MULTIPLIER, Math.max(1, Math.floor((HOUSE_RETURN / (1 - random)) * 100) / 100));
  return multiplier.toFixed(2);
}

export async function verifyCrash(input: {
  serverSeed: string;
  serverSeedHash: string;
  roundId: string | number;
  recordedCrashMultiplier: string;
  settlement?: { wagerUnits: number; cashedAt: string | null; recordedPayoutUnits: number };
}): Promise<VerificationResult<{
  serverSeedHash: string;
  crashMultiplier: string;
  payoutUnits: number | null;
}>> {
  const serverSeedHash = await sha256(input.serverSeed);
  const crashMultiplier = await calculateCrashMultiplier(input.serverSeed, input.roundId);
  const checks: VerificationCheck[] = [
    { key: "commitment", ok: serverSeedHash === input.serverSeedHash, expected: input.serverSeedHash, actual: serverSeedHash },
    { key: "outcome", ok: crashMultiplier === input.recordedCrashMultiplier, expected: input.recordedCrashMultiplier, actual: crashMultiplier }
  ];
  let payoutUnits: number | null = null;
  if (input.settlement) {
    if (!Number.isSafeInteger(input.settlement.wagerUnits) || input.settlement.wagerUnits < 0) throw new Error("INVALID_WAGER");
    const cashedAt = input.settlement.cashedAt === null ? null : Number(input.settlement.cashedAt);
    if (cashedAt !== null && (!Number.isFinite(cashedAt) || cashedAt < 1)) throw new Error("INVALID_CASHOUT");
    payoutUnits = cashedAt !== null && cashedAt <= Number(crashMultiplier)
      ? Math.round((input.settlement.wagerUnits * cashedAt) / 10) * 10
      : 0;
    checks.push({ key: "payout", ok: payoutUnits === input.settlement.recordedPayoutUnits, expected: input.settlement.recordedPayoutUnits, actual: payoutUnits });
  }
  return result({ serverSeedHash, crashMultiplier, payoutUnits }, checks);
}
